import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import MemoryIcon from "@mui/icons-material/Memory";
import LayersIcon from "@mui/icons-material/Layers";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { fetchDiagnosis } from "./api";
import { isLocalDashboard } from "../OrgUsageAnalytics/api";
import {
  describeMemoryTrend,
  describeQueue,
  failureCodes,
  formatCount,
  formatDuration,
  formatPercent,
  shortRevision,
} from "./format";
import "./GcpMonitoring.css";

// "local" is only offered on a dev machine, where it targets a backend running
// on this machine. It is the default there because these routes reach prod and
// dev only once they are deployed.
const ENVS = isLocalDashboard() ? ["local", "prod", "dev"] : ["prod", "dev"];
const DEFAULT_ENV = ENVS[0];

const WINDOWS = [
  { value: "1h", label: "Last 1 hr" },
  { value: "6h", label: "Last 6 hrs" },
  { value: "24h", label: "Last 24 hrs" },
  { value: "7d", label: "Last 7 days" },
];

const COLORS = {
  critical: "#ef4444",
  warning: "#f59e0b",
  ok: "#10b981",
  primary: "#4f46e5",
  neutral: "#94a3b8",
};

const utilizationColor = (fraction) => {
  if (fraction >= 0.9) {
    return COLORS.critical;
  }
  if (fraction >= 0.75) {
    return COLORS.warning;
  }
  return COLORS.primary;
};

const SEVERITY_LABEL = { critical: "critical", warning: "warning", info: "info" };

function Finding({ finding }) {
  const sevClass = SEVERITY_LABEL[finding.severity] || "info";
  return (
    <div className="gcpmon-finding">
      <span className={`gcpmon-sev ${sevClass}`}>{sevClass}</span>
      <div style={{ flex: 1 }}>
        <p className="gcpmon-finding-title">{finding.title}</p>
        <p className="gcpmon-finding-detail">{finding.detail}</p>
        {finding.consoleUrl ? (
          <a
            className="gcpmon-finding-link"
            href={finding.consoleUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in GCP console →
          </a>
        ) : null}
      </div>
    </div>
  );
}

function DiagnosisCard({ findings, panelErrors, loading }) {
  const errorEntries = Object.entries(panelErrors || {});
  const hasFindings = Array.isArray(findings) && findings.length > 0;

  return (
    <div className="gcpmon-card">
      <h2 className="gcpmon-card-title">Diagnosis</h2>
      <p className="gcpmon-card-sub">
        Everything that crossed a threshold, worst first. Empty is good — it
        means no service, queue, or document reason is currently out of bounds.
      </p>

      {loading && !hasFindings ? (
        <div className="gcpmon-allclear" style={{ color: "#64748b" }}>
          <span className="gcpmon-spinner" /> Analyzing live GCP data…
        </div>
      ) : null}

      {!loading && !hasFindings && errorEntries.length === 0 ? (
        <div className="gcpmon-allclear">
          <CheckCircleOutlineIcon fontSize="small" /> All clear — nothing crossed
          a threshold in this window.
        </div>
      ) : null}

      {hasFindings
        ? findings.map((finding) => (
            <Finding key={finding.id} finding={finding} />
          ))
        : null}

      {errorEntries.map(([panel, err]) => (
        <div className="gcpmon-banner warn" key={panel} style={{ marginTop: 12 }}>
          <strong>{panel}</strong> panel unavailable: {err.reason || err.message}
        </div>
      ))}
    </div>
  );
}

/**
 * `question` is the operator question the panel answers, and `reading` is how
 * to interpret what it shows. Both are spelled out because every number here
 * comes from a Cloud Monitoring metric whose name explains nothing on its own.
 */
function Panel({ icon, title, question, reading, children }) {
  return (
    <div className="gcpmon-card">
      <h3 className="gcpmon-card-title gcpmon-card-title-row">
        {icon} {title}
      </h3>
      {question ? <p className="gcpmon-card-sub">{question}</p> : null}
      {children}
      {reading ? <p className="gcpmon-reading">{reading}</p> : null}
    </div>
  );
}

function EmptyState({ label }) {
  return <div className="gcpmon-empty">{label}</div>;
}

/** Scroll wrapper so wide/tall tables stay usable inside a single-column layout. */
function DataTable({ children }) {
  return <div className="gcpmon-table-scroll">{children}</div>;
}

/** Small status word with a colour, e.g. "mostly failing" / "steady". */
function Pill({ tone, children }) {
  return <span className={`gcpmon-pill ${tone}`}>{children}</span>;
}

const memoryTone = (fraction) => {
  if ((fraction || 0) >= 0.9) {
    return "critical";
  }
  if ((fraction || 0) >= 0.75) {
    return "warning";
  }

  return "ok";
};

/**
 * The four numbers worth knowing before reading any chart. Each one names the
 * subject it refers to (which service, which queue) so the strip stands alone.
 */
function SummaryStrip({ panels, loading }) {
  const worstMemory = (panels.memory?.revisions || [])[0] || null;
  const queues = panels.queues?.queues || [];
  const churning = queues.filter(
    (q) => (q.totalAttempts || 0) >= 20 && (q.retryShare || 0) >= 0.3,
  );
  const backlog = panels.queues?.totals?.totalDepth;
  const errorTotal = panels.errors?.total;
  const terminal = panels.rootCauses?.extraction?.terminal;

  const items = [
    {
      label: "Tightest memory",
      value: worstMemory ? formatPercent(worstMemory.p95) : "—",
      note: worstMemory ? worstMemory.service || "unknown" : "no data",
      tone: worstMemory ? memoryTone(worstMemory.p95) : "neutral",
    },
    {
      label: "Queues churning",
      value: formatCount(churning.length),
      note: churning.length ? churning[0].queueId : "none failing heavily",
      tone: churning.length ? "warning" : "ok",
    },
    {
      label: "Tasks waiting",
      value: backlog === undefined ? "—" : formatCount(backlog),
      note: `across ${formatCount(queues.length)} queue(s)`,
      tone: (backlog || 0) >= 500 ? "warning" : "ok",
    },
    {
      label: "Errors logged",
      value: errorTotal === undefined ? "—" : formatCount(errorTotal),
      note:
        terminal === undefined
          ? "in this window"
          : `${formatCount(terminal)} terminal doc failure(s)`,
      tone: (errorTotal || 0) > 0 ? "warning" : "ok",
    },
  ];

  return (
    <div className="gcpmon-summary">
      {items.map((item) => (
        <div className="gcpmon-summary-item" key={item.label}>
          <span className="gcpmon-summary-label">{item.label}</span>
          <span className={`gcpmon-summary-value ${item.tone}`}>
            {loading && item.value === "—" ? "…" : item.value}
          </span>
          <span className="gcpmon-summary-note">{item.note}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Collapse revisions to the worst one per service. Cloud Run keeps old
 * revisions reporting, so an unfiltered "worst 10" is usually three revisions
 * of the same service — which buries how many distinct services are in
 * trouble.
 */
const worstPerService = (revisions) => {
  const byService = new Map();

  for (const rev of revisions) {
    const service = rev.service || "unknown";
    const existing = byService.get(service);
    if (!existing || (rev.p95 || 0) > (existing.p95 || 0)) {
      byService.set(service, rev);
    }
  }

  return [...byService.values()].sort((a, b) => (b.p95 || 0) - (a.p95 || 0));
};

function MemoryPanel({ data }) {
  const revisions = useMemo(() => data?.revisions || [], [data]);
  const services = useMemo(() => worstPerService(revisions), [revisions]);
  const chartData = useMemo(
    () =>
      services.slice(0, 10).map((rev) => ({
        name: rev.service || "unknown",
        p95: Math.round((rev.p95 || 0) * 100),
        fraction: rev.p95 || 0,
      })),
    [services],
  );

  return (
    <Panel
      icon={<MemoryIcon fontSize="small" htmlColor={COLORS.primary} />}
      title="Cloud Run memory"
      question="Is any service running out of memory?"
      reading="100% means the container has reached its configured memory limit and Cloud Run kills it. Amber starts at 75%, red at 90%."
    >
      {chartData.length === 0 ? (
        <EmptyState label="No memory data reported for this window." />
      ) : (
        <>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 12, left: 0, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11 }}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={64}
              />
              <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value) => [`${value}% of limit`, "Peak (p95)"]}
              />
              <Bar
                dataKey="p95"
                name="Peak memory used (p95)"
                radius={[4, 4, 0, 0]}
              >
                {chartData.map((entry) => (
                  <Cell
                    key={entry.name}
                    fill={utilizationColor(entry.fraction)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <DataTable>
            <table className="gcpmon-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Revision</th>
                  <th className="num">Now</th>
                  <th className="num">Peak</th>
                  <th>Trend</th>
                </tr>
              </thead>
              <tbody>
                {revisions.slice(0, 8).map((rev) => {
                  const trend = describeMemoryTrend(rev);

                  return (
                    <tr key={`${rev.service}-${rev.revision}`}>
                      <td>{rev.service || "unknown"}</td>
                      <td className="gcpmon-mono">
                        {shortRevision(rev.service, rev.revision)}
                      </td>
                      <td className="num">{formatPercent(rev.current)}</td>
                      <td className="num">{formatPercent(rev.p95)}</td>
                      <td>
                        <Pill tone={trend.tone}>{trend.label}</Pill>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </DataTable>

          {data?.heap?.enabled ? (
            <p className="gcpmon-card-sub" style={{ marginTop: 10 }}>
              Heap sampler active on {data.heap.revisions.length} revision(s),
              so V8 heap pressure can be told apart from off-heap memory.
            </p>
          ) : null}
        </>
      )}
    </Panel>
  );
}

function QueuePanel({ data }) {
  const queues = (data?.queues || [])
    .filter((q) => q.currentDepth > 0 || q.totalAttempts > 0)
    .slice(0, 10);

  // Succeeded vs failed attempts, stacked. The previous version charted
  // backlog depth next to retry share as a percentage — two different units on
  // one axis, which made a 100%-failing queue look identical to a 100-deep
  // backlog. Both bars here are attempt counts, so the split is literal.
  const chartData = useMemo(
    () =>
      queues.map((q) => {
        const total = Math.round(q.totalAttempts || 0);
        const failed = Math.round(q.failedAttempts || 0);

        return {
          name: q.queueId,
          succeeded: Math.max(total - failed, 0),
          failed,
        };
      }),
    [queues],
  );

  return (
    <Panel
      icon={<LayersIcon fontSize="small" htmlColor={COLORS.primary} />}
      title="Cloud Tasks queues"
      question="Is background work piling up, or failing over and over?"
      reading="Backlog is what is waiting right now; attempts are what ran during the window. A queue with an empty backlog but all-failed attempts is churning, not idle — the response code says why."
    >
      {chartData.length === 0 ? (
        <EmptyState label="No queue activity in this window." />
      ) : (
        <>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 12, left: 0, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11 }}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={72}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                dataKey="succeeded"
                stackId="attempts"
                name="Succeeded"
                fill={COLORS.ok}
              />
              <Bar
                dataKey="failed"
                stackId="attempts"
                name="Failed"
                fill={COLORS.critical}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>

          <DataTable>
            <table className="gcpmon-table gcpmon-table--wide">
              <thead>
                <tr>
                  <th>Queue</th>
                  <th>Status</th>
                  <th className="num">Waiting</th>
                  <th className="num">Attempts</th>
                  <th className="num">Failed</th>
                  <th className="num">Longest wait</th>
                  <th>Failing with</th>
                </tr>
              </thead>
              <tbody>
                {queues.map((q) => {
                  const status = describeQueue(q);
                  const codes = failureCodes(q.attemptsByCode);
                  const total = Math.round(q.totalAttempts || 0);
                  const failed = Math.round(q.failedAttempts || 0);

                  return (
                    <tr key={q.queueId}>
                      <td>{q.queueId}</td>
                      <td>
                        <Pill tone={status.tone}>{status.label}</Pill>
                      </td>
                      <td className="num">{formatCount(q.currentDepth)}</td>
                      <td className="num">{formatCount(total)}</td>
                      <td className="num">
                        {failed ? `${formatCount(failed)} (${formatPercent(q.retryShare)})` : "0"}
                      </td>
                      <td className="num">{formatDuration(q.p95DelaySeconds)}</td>
                      <td className="gcpmon-mono">
                        {codes.length
                          ? codes
                              .map((entry) => `${entry.code} ×${entry.count}`)
                              .join(", ")
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </DataTable>
        </>
      )}
    </Panel>
  );
}

function ErrorPanel({ data }) {
  // Lead with the service, not the route. Most Cloud Run error logs are thrown
  // from application code rather than an HTTP request handler, so they carry no
  // requestUrl and all collapse into one "(no route)" bucket — which named
  // nothing. The service label is always present.
  const byService = (data?.byService || []).slice(0, 10);
  const chartData = useMemo(
    () => byService.map((bucket) => ({ name: bucket.key, count: bucket.count })),
    [byService],
  );
  const namedRoutes = (data?.byRoute || []).filter(
    (bucket) => bucket.key && bucket.key !== "(no route)",
  );
  const unroutedCount =
    (data?.byRoute || []).find((bucket) => bucket.key === "(no route)")?.count ||
    0;

  return (
    <Panel
      icon={
        <ReportProblemOutlinedIcon fontSize="small" htmlColor={COLORS.critical} />
      }
      title="Cloud Run errors"
      question="Which service is logging errors, and what are they?"
      reading={
        unroutedCount
          ? `${formatCount(unroutedCount)} of these were logged outside an HTTP request handler, so they have no route to attribute — the service and message identify them instead.`
          : "Counts are ERROR-severity log entries, so one failing request can log more than once."
      }
    >
      {chartData.length === 0 ? (
        <EmptyState label="No errors logged in this window." />
      ) : (
        <>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              layout="vertical"
              data={chartData}
              margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="name"
                width={150}
                tick={{ fontSize: 11 }}
              />
              <Tooltip />
              <Bar
                dataKey="count"
                name="Errors"
                fill={COLORS.critical}
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>

          <DataTable>
            <table className="gcpmon-table gcpmon-table--wide">
              <thead>
                <tr>
                  <th>Service</th>
                  <th className="num">Errors</th>
                  <th className="num">Share</th>
                  <th>Example</th>
                </tr>
              </thead>
              <tbody>
                {byService.map((bucket) => (
                  <tr key={bucket.key}>
                    <td>{bucket.key}</td>
                    <td className="num">{formatCount(bucket.count)}</td>
                    <td className="num">{formatPercent(bucket.share)}</td>
                    <td
                      className="gcpmon-message"
                      title={bucket.sampleMessage || ""}
                    >
                      {bucket.sampleMessage || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTable>

          {namedRoutes.length ? (
            <p className="gcpmon-card-sub" style={{ marginTop: 12 }}>
              Busiest failing routes:{" "}
              {namedRoutes
                .slice(0, 3)
                .map((bucket) => `${bucket.key} (${formatCount(bucket.count)})`)
                .join(", ")}
            </p>
          ) : null}
        </>
      )}
    </Panel>
  );
}

function RootCausePanel({ data }) {
  const extraction = data?.extraction || { categories: [] };
  const categories = (extraction.categories || []).slice(0, 10);
  const chartData = useMemo(
    () =>
      categories.map((c) => ({
        name: c.reason,
        count: c.count,
        color: c.wastedRetry
          ? COLORS.critical
          : c.retryable
            ? COLORS.warning
            : COLORS.neutral,
      })),
    [categories],
  );

  return (
    <Panel
      icon={
        <FindInPageOutlinedIcon fontSize="small" htmlColor={COLORS.primary} />
      }
      title="Document failures"
      question="Why are documents failing to process?"
      reading={`${formatCount(extraction.terminal)} will never succeed on retry (terminal) and ${formatCount(extraction.retryable)} may. Red bars are terminal failures the queue retries anyway — wasted work worth fixing first.`}
    >
      {chartData.length === 0 ? (
        <EmptyState label="No document failures recorded in this window." />
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis
              type="category"
              dataKey="name"
              width={180}
              tick={{ fontSize: 11 }}
            />
            <Tooltip />
            <Bar dataKey="count" name="Documents" radius={[0, 4, 4, 0]}>
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

export default function GcpMonitoringPage({ onReady }) {
  const [windowValue, setWindowValue] = useState("6h");
  const [env, setEnv] = useState(DEFAULT_ENV);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (onReady) {
      onReady();
    }
  }, [onReady]);

  const load = useCallback(
    async (refresh = false) => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchDiagnosis(windowValue, env, refresh);
        setData(result);
      } catch (err) {
        setError(err?.message || "Failed to load GCP monitoring data.");
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [windowValue, env],
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const panels = data?.panels || {};

  return (
    <div className="gcpmon-page">
      <div className="gcpmon-header">
        <div>
          <h1 className="gcpmon-title">GCP Monitoring</h1>
          <p className="gcpmon-subtitle">
            What is degrading right now, and why. Reads live Cloud Run and Cloud
            Tasks data for each deployed service — client, backend, and the rest
            — then ranks whatever crossed a threshold, worst first.
          </p>
        </div>
        <div className="gcpmon-controls">
          <div className="gcpmon-toggle" role="group" aria-label="Environment">
            {ENVS.map((value) => (
              <button
                key={value}
                type="button"
                className={env === value ? "active" : ""}
                onClick={() => setEnv(value)}
              >
                {value.toUpperCase()}
              </button>
            ))}
          </div>
          <select
            className="gcpmon-select"
            value={windowValue}
            onChange={(e) => setWindowValue(e.target.value)}
            aria-label="Time window"
          >
            {WINDOWS.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="gcpmon-btn gcpmon-btn-primary"
            onClick={() => load(true)}
            disabled={loading}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            {loading ? (
              <span className="gcpmon-spinner" style={{ borderTopColor: "#fff" }} />
            ) : (
              <RefreshIcon fontSize="small" />
            )}
            Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="gcpmon-banner error">
          {error}
        </div>
      ) : null}

      <SummaryStrip panels={panels} loading={loading} />

      <DiagnosisCard
        findings={data?.findings}
        panelErrors={data?.panelErrors}
        loading={loading}
      />

      <div className="gcpmon-grid">
        <MemoryPanel data={panels.memory} />
        <QueuePanel data={panels.queues} />
        <ErrorPanel data={panels.errors} />
        <RootCausePanel data={panels.rootCauses} />
      </div>
    </div>
  );
}
